/**
 * ATS Login Page
 * Handles magic link token-based authentication and SSO token authentication
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Link2, ArrowRight } from 'lucide-react';

const ATSLogin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { loginWithToken } = useAuth();
    
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [error, setError] = useState('');
    
    // Support both 'token' (magic link) and 'ssotoken' (SSO token)
    const magicToken = searchParams.get('token');
    const ssoToken = searchParams.get('ssotoken');

    const validateMagicToken = useCallback(async () => {
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/ats/login/${magicToken}`, {
                credentials: 'include' // Important for httpOnly cookies
            });
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || 'Login failed');
            }
            
            // The backend has set httpOnly cookies. 
            // Now update React auth state using the cookie-based authentication.
            await loginWithToken(null);
            
            setStatus('success');
            
            // Redirect to dashboard after a brief delay
            setTimeout(() => {
                window.location.href = '/dashboard';
            }, 1500);
            
        } catch (err) {
            setStatus('error');
            setError(err.message || 'Failed to validate login token');
        }
    }, [magicToken, loginWithToken]);

    const validateSsoToken = useCallback(async () => {
        try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${API_URL}/api/ats/sso-login/${ssoToken}`, {
                credentials: 'include' // Important for httpOnly cookies
            });
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || 'SSO Login failed');
            }
            
            // The backend has set httpOnly cookies. 
            // Now update React auth state using the cookie-based authentication.
            await loginWithToken(null);
            
            setStatus('success');
            
            // Redirect to dashboard after a brief delay
            setTimeout(() => {
                window.location.href = '/dashboard';
            }, 1500);
            
        } catch (err) {
            setStatus('error');
            setError(err.message || 'Failed to validate SSO token');
        }
    }, [ssoToken, loginWithToken]);

    useEffect(() => {
        if (ssoToken) {
            validateSsoToken();
        } else if (magicToken) {
            validateMagicToken();
        } else {
            setStatus('error');
            setError('No login token provided');
        }
    }, [magicToken, ssoToken, validateMagicToken, validateSsoToken]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Link2 className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">
                        {status === 'loading' && 'Signing You In...'}
                        {status === 'success' && 'Welcome!'}
                        {status === 'error' && 'Login Failed'}
                    </CardTitle>
                    <CardDescription>
                        {status === 'loading' && 'Validating your login link'}
                        {status === 'success' && 'Redirecting to your dashboard'}
                        {status === 'error' && error}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-4">
                    {status === 'loading' && (
                        <Loader2 className="h-12 w-12 animate-spin text-primary" />
                    )}
                    
                    {status === 'success' && (
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center">
                                <CheckCircle className="h-8 w-8 text-emerald-500" />
                            </div>
                            <Button onClick={() => navigate('/dashboard')} className="gap-2">
                                Go to Dashboard
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                    
                    {status === 'error' && (
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
                                <XCircle className="h-8 w-8 text-red-500" />
                            </div>
                            <div className="space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    This could happen if:
                                </p>
                                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                                    <li>The link has expired</li>
                                    <li>The link has already been used</li>
                                    <li>Your account is deactivated</li>
                                </ul>
                            </div>
                            <Button variant="outline" onClick={() => navigate('/login')}>
                                Go to Regular Login
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ATSLogin;
